import React, { useCallback, useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { DatePicker } from '@mui/x-date-pickers-pro';

import ImageUploadBox from '~/components/CustomerCommunication/HeroPlanner/Components/ScheduleForms/Inputs/ImageUploadBox';

import useImageUrlGenerator from '~/hooks/useImageUrlGenerator';

import CorporateEventsService, { CreateEventParams, Event } from '~/services/CorporateEventsService';
import { uploadImage } from '~/services/ImageService';
import { Dayjs, newDate } from '~/services/TimeService';

const DEFAULT_DESCRIPTION =
  "We're excited to welcome you to this event, proudly hosted in partnership with Luxury Escapes at the stunning [Hotel] in [Location]. Simply fill out the registration form below so we can tailor your experience and capture any special requirements.";

interface EventFormProps {
  event?: Event;
  onSubmit: (data: CreateEventParams) => Promise<void>;
  onCancel?: () => void;
  isSubmitting?: boolean;
  onHasUnsavedChangesChange?: (hasChanges: boolean) => void;
}

export default function EventForm({
  event,
  onSubmit,
  onCancel,
  isSubmitting = false,
  onHasUnsavedChangesChange,
}: EventFormProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { data: companies = [] } = useQuery({
    queryKey: ['corporate-events', 'companies'],
    queryFn: () => CorporateEventsService.getCompanies(),
  });

  const getInitialFormData = (): CreateEventParams => ({
    companyId: '',
    name: '',
    description: DEFAULT_DESCRIPTION,
    heroImageId: '',
    hotelName: '',
    roomType: '',
    roomInclusions: '',
    checkinDate: '',
    checkoutDate: '',
    locationCity: '',
    locationCountry: '',
    itineraryHighlights: '',
    agendaFileUrl: '',
    dressCode: '',
    faqsUrl: '',
    allowEditRegistration: false,
    allowTripExtension: false,
    allowGuest: false,
    allowPreferredAirline: false,
    allowPreferredSeatClass: false,
  });

  const [formData, setFormData] = useState<CreateEventParams>(getInitialFormData);

  const [initialFormData, setInitialFormData] = useState<CreateEventParams>(getInitialFormData);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const heroImageUrl = useImageUrlGenerator(formData.heroImageId || '');

  useEffect(() => {
    if (event) {
      const eventData: CreateEventParams = {
        companyId: event.companyId,
        name: event.name,
        description: event.description || '',
        heroImageId: event.heroImageId || '',
        hotelName: event.hotelName || '',
        roomType: event.roomType || '',
        roomInclusions: event.roomInclusions || '',
        checkinDate: event.checkinDate || '',
        checkoutDate: event.checkoutDate || '',
        locationCity: event.locationCity || '',
        locationCountry: event.locationCountry || '',
        itineraryHighlights: event.itineraryHighlights || '',
        agendaFileUrl: event.agendaFileUrl || '',
        dressCode: event.dressCode || '',
        faqsUrl: event.faqsUrl || '',
        allowEditRegistration: event.allowEditRegistration,
        allowTripExtension: event.allowTripExtension || false,
        allowGuest: event.allowGuest || false,
        allowPreferredAirline: event.allowPreferredAirline || false,
        allowPreferredSeatClass: event.allowPreferredSeatClass || false,
      };
      setFormData(eventData);
      setInitialFormData(eventData);
    }
  }, [event]);

  // Detect unsaved changes
  useEffect(() => {
    if (!onHasUnsavedChangesChange) {
      return;
    }

    const hasChanges = JSON.stringify(formData) !== JSON.stringify(initialFormData);
    onHasUnsavedChangesChange(hasChanges);
  }, [formData, initialFormData, onHasUnsavedChangesChange]);

  const handleChange =
    (field: keyof CreateEventParams) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const handleCheckboxChange =
    (field: keyof CreateEventParams) => (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setFormData((prev) => ({ ...prev, [field]: checked }));
    };

  const handleDateChange = (field: 'checkinDate' | 'checkoutDate') => (value: Dayjs | null) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value ? value.format('YYYY-MM-DD') : '',
    }));
  };

  const handleImageUpload = useCallback(
    async (file: File) => {
      setIsUploadingImage(true);
      try {
        const response = await uploadImage(file);
        setFormData((prev) => ({ ...prev, heroImageId: response.public_id }));
        enqueueSnackbar('Image uploaded successfully', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar(`Failed to upload image: ${error instanceof Error ? error.message : 'Unknown error'}`, {
          variant: 'error',
        });
      } finally {
        setIsUploadingImage(false);
      }
    },
    [enqueueSnackbar],
  );

  const handleRemoveImage = useCallback(() => {
    setFormData((prev) => ({ ...prev, heroImageId: '' }));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(formData);
    // Clear unsaved changes flag
    setInitialFormData(formData);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={3}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Company"
              select
              value={formData.companyId}
              onChange={(e) => setFormData((prev) => ({ ...prev, companyId: e.target.value }))}
              required
            >
              {companies.map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  {company.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField fullWidth label="Event Name" value={formData.name} onChange={handleChange('name')} required />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={handleChange('description')}
              multiline
              rows={3}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField fullWidth label="Hotel Name" value={formData.hotelName} onChange={handleChange('hotelName')} />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField fullWidth label="Room Type" value={formData.roomType} onChange={handleChange('roomType')} />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              label="Room Inclusions"
              value={formData.roomInclusions}
              onChange={handleChange('roomInclusions')}
              multiline
              rows={2}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <DatePicker
              label="Check-in Date"
              value={formData.checkinDate ? newDate(formData.checkinDate) : null}
              onChange={handleDateChange('checkinDate')}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <DatePicker
              label="Check-out Date"
              value={formData.checkoutDate ? newDate(formData.checkoutDate) : null}
              onChange={handleDateChange('checkoutDate')}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField fullWidth label="City" value={formData.locationCity} onChange={handleChange('locationCity')} />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Country"
              value={formData.locationCountry}
              onChange={handleChange('locationCountry')}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              label="Itinerary Highlights"
              value={formData.itineraryHighlights}
              onChange={handleChange('itineraryHighlights')}
              multiline
              rows={3}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Agenda File URL"
              value={formData.agendaFileUrl}
              onChange={handleChange('agendaFileUrl')}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField fullWidth label="Dress Code" value={formData.dressCode} onChange={handleChange('dressCode')} />
          </Grid>

          <Grid size={12}>
            <TextField fullWidth label="FAQs URL" value={formData.faqsUrl} onChange={handleChange('faqsUrl')} />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.allowEditRegistration}
                  onChange={handleCheckboxChange('allowEditRegistration')}
                />
              }
              label="Allow employees to edit their registration"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Checkbox checked={formData.allowTripExtension} onChange={handleCheckboxChange('allowTripExtension')} />
              }
              label="Allow Trip Extension"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={<Checkbox checked={formData.allowGuest} onChange={handleCheckboxChange('allowGuest')} />}
              label="Allow Guest"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.allowPreferredAirline}
                  onChange={handleCheckboxChange('allowPreferredAirline')}
                />
              }
              label="Allow Preferred Airline"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.allowPreferredSeatClass}
                  onChange={handleCheckboxChange('allowPreferredSeatClass')}
                />
              }
              label="Allow Preferred Seat Class"
            />
          </Grid>

          <Grid size={12}>
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Hero Image
              </Typography>
              <Box
                sx={{
                  '& .MuiPaper-root': {
                    maxHeight: 400,
                    overflow: 'hidden',
                  },
                  '& .MuiPaper-root img': {
                    maxHeight: 400,
                    maxWidth: '100%',
                    objectFit: 'contain',
                  },
                }}
              >
                <ImageUploadBox
                  id="hero-image-upload"
                  name="heroImageId"
                  label="hero image"
                  imageUrl={heroImageUrl}
                  onUpload={handleImageUpload}
                  onRemove={handleRemoveImage}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Box>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            {onCancel && (
              <Button variant="text" onClick={onCancel} disabled={isSubmitting || isUploadingImage}>
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting || isUploadingImage}
              startIcon={isUploadingImage ? <CircularProgress size={20} /> : undefined}
            >
              {isUploadingImage
                ? 'Uploading image...'
                : isSubmitting
                ? 'Saving...'
                : event
                ? 'Update Event'
                : 'Create Event'}
            </Button>
          </Stack>
        </Box>
      </Stack>
    </form>
  );
}
